
import { create } from 'zustand'

/**
 * Appearance preference for the application.
 *
 * <p>Light is the default, matching the rest of the Nevtan suite: a new account
 * starts light rather than inheriting whatever the operating system happens to
 * be set to. Choosing dark is remembered and survives sign-out.
 */
export type Theme = 'light' | 'dark'

const DEFAULT_THEME: Theme = 'light'
const STORAGE_KEY = 'nevtan.theme'

function apply(theme: Theme) {
  document.documentElement.dataset.theme = theme
}

function stored(): Theme {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'dark' ? 'dark' : DEFAULT_THEME
  } catch {
    // Private windows and blocked site data both throw here; the default is
    // still perfectly usable, so this must never break start-up.
    return DEFAULT_THEME
  }
}

interface ThemeState {
  theme: Theme
  setTheme: (theme: Theme) => void
  /** Applies the saved choice; call once as the app starts. */
  restore: () => void
}

export const useTheme = create<ThemeState>((set) => ({
  theme: DEFAULT_THEME,

  setTheme: (theme) => {
    apply(theme)
    try {
      localStorage.setItem(STORAGE_KEY, theme)
    } catch {
      // The choice still applies for this session.
    }
    set({ theme })
  },

  restore: () => {
    const theme = stored()
    apply(theme)
    set({ theme })
  },
}))

/*
 * There is deliberately no `usePinnedLightTheme` any more. The marketing pages
 * and the sign-in screen used to force light because the suite was light-only;
 * they now carry the same switch the application does, so one saved preference
 * follows the visitor from the landing page all the way into a conversation.
 */
